import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';

@registerElement('add-remove-seat')
export default class AddRemoveSeatElement extends ImpulseElement {
  /**
   * The interval to bill the customer.
   */
  @property() billingInterval: 'month' | 'year' = 'month';

  /**
   * The number of seats that the entity wants to purchase.
   * E.g., 0/15. Here "15" is the totalSeatsCount.
   */
  @property({ type: Number }) totalSeats = 0;

  /**
   * The cost of 1 seat in dollars per month.
   */
  @property({ type: Number }) costPerSeatMonthly: number;

  /**
   * The cost of 1 seat in dollars per year.
   */
  @property({ type: Number }) costPerSeatYearly: number;

  /**
   * The name of the plan/add-on. E.g., Professional plan, Teletherapy add-on, etc.
   */
  @property() planName: string;

  /**
   * Whether the element is disabled or not.
   */
  @property({ type: Boolean }) disabled = false;

  @target() seatInput: HTMLInputElement;
  @target() removeSeatButton: HTMLButtonElement;

  connected() {
    this.emit('change', { detail: this.getSeatHash });
  }

  /**
   * Called when `billingInterval` property changes.
   */
  billingIntervalChanged() {
    // Re-calculate costs and let the parent listeners know about the change.
    this.emit('change', { detail: this.getSeatHash });
  }

  /**
   * Called when `totalSeatsCount` property changes.
   */
  totalSeatsChanged(newValue: number) {
    this.seatInput.value = newValue.toString();
    this.removeSeatButton.disabled = !newValue;
  }

  /**
   * Called when `disabled` property changes.
   */
  disabledChanged(disabled: boolean) {
    this.seatInput.disabled = disabled;
  }

  /**
   * See `data-action="click->add-remove-seat#addSeat"`
   */
  addSeat() {
    if (this.disabled) return;
    this.totalSeats += 1;
    this.emit('change', { detail: this.getSeatHash });
  }

  /**
   * See `data-action="click->add-remove-seat#removeSeat"`
   */
  removeSeat() {
    if (this.disabled || this.totalSeats === 0) return;

    this.totalSeats -= 1;
    this.emit('change', { detail: this.getSeatHash });
  }

  setSeats(event: Event) {
    const input = event.target as HTMLInputElement;
    this.totalSeats = Number(input.value);
    this.emit('change', { detail: this.getSeatHash });
  }

  private get getSeatHash() {
    return {
      name: this.planName,
      totalSeatsCount: this.totalSeats,
      costPerSeat: this.billingInterval === 'month' ? this.costPerSeatMonthly : this.costPerSeatYearly,
    };
  }
}
